import {useState, useMemo} from 'react'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/he'
import 'dayjs/locale/en'
import {heIL, enUS} from '@mui/x-date-pickers/locales'
import storage from 'services/storage'

const RelationshipUpdateDateTrigger = ({fieldName, labelKey, actionKey}) => {
    const {t} = useTranslation()
    const {watch, setValue} = useFormContext()
    const currentValue = watch(fieldName)
    const [open, setOpen] = useState(false)
    const [tempValue, setTempValue] = useState(() => (currentValue ? dayjs(currentValue) : null))

    const currentLanguage = storage.getLanguage() || 'he'
    const localeMap = useMemo(() => ({he: heIL, en: enUS}), [])
    const currentLocale = localeMap[currentLanguage] || localeMap.he

    const handleOpen = (event) => {
        event?.preventDefault?.()
        event?.stopPropagation?.()
        setTempValue(currentValue ? dayjs(currentValue) : null)
        setOpen(true)
    }

    const handleClose = () => setOpen(false)

    const handleSave = () => {
        setValue(fieldName, tempValue ? tempValue.toISOString() : null, {shouldDirty: true})
        setOpen(false)
    }

    const handleClear = () => {
        setTempValue(null)
        setValue(fieldName, null, {shouldDirty: true})
        setOpen(false)
    }

    const dialogTitle = t(`meters.${labelKey}`)
    const tooltipLabel = currentValue
        ? t('meters.updateDateTooltipWithValue', {
              field: dialogTitle,
              value: dayjs(currentValue).format('DD/MM/YYYY HH:mm'),
          })
        : t(`meters.${actionKey}`)

    return (
        <>
            <Tooltip title={tooltipLabel}>
                <IconButton
                    size="small"
                    onClick={handleOpen}
                    color={currentValue ? 'primary' : 'default'}>
                    <EditCalendarRoundedIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={currentLanguage}
                        localeText={
                            currentLocale.components.MuiLocalizationProvider.defaultProps.localeText
                        }>
                        <DateTimePicker
                            value={tempValue}
                            onChange={(newValue) => setTempValue(newValue)}
                            views={['year', 'month', 'day', 'hours', 'minutes']}
                            format="DD/MM/YYYY HH:mm"
                            ampm={false}
                            slotProps={{
                                textField: {
                                    variant: 'standard',
                                    fullWidth: true,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClear}>{t('modal.clear')}</Button>
                    <Button onClick={handleClose}>{t('modal.cancel')}</Button>
                    <Button onClick={handleSave} variant="contained">
                        {t('modal.submit')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default RelationshipUpdateDateTrigger
